import { ValidatorForm } from "react-material-ui-form-validator";
import { IFormFieldKeyLookupManage } from "../_common/models/formFieldKeyLookup";
import {
    FormFieldSpecialType,
    getSubsectionFieldValue,
    occupancyConflicts,
    toIsoDateValue,
    visitOccupancy
} from "./sessionPeriod.helper";

export const CORRECT_ANSWER_VALIDATION_MESSAGE = "You must contact the specialist before continuing.";

export function getCorrectAnswerValidationRuleName(fieldId: number) {
    return `correctAnswer_${fieldId}`;
}

export function getCorrectAnswerLookupValues(lookups?: Array<IFormFieldKeyLookupManage>) {
    return (lookups ?? [])
        .filter(lookup => lookup.isCorrectAnswer)
        .map(lookup => lookup.lookupValue);
}

export function hasCorrectAnswerRule(lookups?: Array<IFormFieldKeyLookupManage>) {
    return getCorrectAnswerLookupValues(lookups).length > 0;
}

export function isCorrectAnswerValueSelected(value: string | null | undefined, lookups?: Array<IFormFieldKeyLookupManage>) {
    const correctAnswers = getCorrectAnswerLookupValues(lookups);
    if (!correctAnswers.length) {
        return true;
    }

    const normalizedValue = (value ?? "").trim().toLowerCase();
    if (!normalizedValue) {
        return false;
    }

    return correctAnswers.some(answer => answer.trim().toLowerCase() === normalizedValue);
}

export function registerCorrectAnswerValidationRule(ruleName: string, correctAnswers: string[]) {
    ValidatorForm.removeValidationRule(ruleName);
    ValidatorForm.addValidationRule(ruleName, (value) => {
        if (!correctAnswers.length) {
            return true;
        }

        const normalizedValue = (value ?? "").trim();
        if (!normalizedValue) {
            return true;
        }

        return correctAnswers.some(answer => answer.trim().toLowerCase() === normalizedValue.toLowerCase());
    });
}

export function removeCorrectAnswerValidationRule(ruleName: string) {
    ValidatorForm.removeValidationRule(ruleName);
}

export function getIncorrectCorrectAnswerFields(formFieldKeys: any[]) {
    return (formFieldKeys ?? []).filter(ffk => {
        if (!hasCorrectAnswerRule(ffk.formFieldKeyLookups)) {
            return false;
        }

        const value = (ffk.formField?.fieldValue ?? "").trim();
        if (!value) {
            return false;
        }

        return !isCorrectAnswerValueSelected(value, ffk.formFieldKeyLookups);
    });
}

export const applyFileValidationRule = ({
    isEditing,
    hasExistingFile,
    hasNewFile
}: {
    isEditing: boolean;
    hasExistingFile: boolean;
    hasNewFile: boolean;
}) => {
    ValidatorForm.addValidationRule("fileRequired", () => {

        if (!isEditing) {
            return hasNewFile; 
        }

        if (hasExistingFile) {
            return true;
        }
        return hasNewFile;
    });
};

export const removeFileValidationRule = () => {
    ValidatorForm.removeValidationRule("fileRequired");
};

export function getDuplicateValues(values: Array<string | null | undefined>): string[] {
    const normalized = (values ?? [])
        .map(v => (v ?? "").trim())
        .filter(v => v.length > 0);

    const counts: Record<string, number> = {};
    for (let i = 0; i < normalized.length; i++) {
        const v = normalized[i];
        counts[v] = (counts[v] ?? 0) + 1;
    }

    const duplicates: string[] = [];
    for (const key in counts) {
        if (Object.prototype.hasOwnProperty.call(counts, key) && counts[key] > 1) {
            duplicates.push(key);
        }
    }

    return duplicates;
}


export function getDuplicateVisitDatesFromFormFieldKeys(formFieldKeys: any[], schoolTypeName?: string | null): string[] {
    const visits = (formFieldKeys ?? [])
        .filter(ffk => ffk.formFieldKeySpecialTypeId === FormFieldSpecialType.SessionDateTime)
        .map(ffk => {
            const date = toIsoDateValue(ffk.formField?.fieldValue);
            if (!date) return null;
            return {
                date,
                occupancy: visitOccupancy(
                    getSubsectionFieldValue(formFieldKeys, ffk.subSectionId, FormFieldSpecialType.SessionTypeName),
                    getSubsectionFieldValue(formFieldKeys, ffk.subSectionId, FormFieldSpecialType.SessionPeriod),
                    schoolTypeName
                )
            };
        })
        .filter((v): v is { date: string; occupancy: string } => !!v);

    const conflicts: string[] = [];
    const byDate: Record<string, string[]> = {};
    visits.forEach(visit => {
        if (!byDate[visit.date]) byDate[visit.date] = [];
        byDate[visit.date].push(visit.occupancy);
    });

    Object.keys(byDate).forEach(date => {
        const occupancies = byDate[date];
        for (let i = 0; i < occupancies.length; i++) {
            for (let j = i + 1; j < occupancies.length; j++) {
                if (occupancyConflicts(occupancies[i], occupancies[j])) {
                    conflicts.push(date);
                    return;
                }
            }
        }
    });

    return conflicts;
}
